/**
 * CR098/CR225/CR229: Section Order Panel
 * 
 * Drag-and-drop reordering of card sections.
 * 
 * CR225: This panel controls ORDERING ONLY.
 * Visibility toggles are managed by VisibilityPanel (single source of truth).
 * The eye icon here shows current visibility state but toggling it
 * delegates to the VisibilityPanel's onChange handler.
 * 
 * CR229: Adds isDragging state to pause preview updates during drag.
 */

import { useState, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { GripVertical, List, RotateCcw, Eye, EyeOff } from "lucide-react";
import type { CardVisibility } from "@/lib/deals/cardVisibility";

// ================================================================
// TYPES
// ================================================================

export interface SectionOrderItem {
  id: string;
  label: string;
  enabled: boolean;
}

// Default section order
// CR423: Use canonical keys (value_box, spend_box) not legacy (effective_value_box, qualifying_spend_box)
// CR424: Uses canonical block IDs only (no phantom sections)
export const DEFAULT_SECTION_ORDER: string[] = [
  "ribbon",
  "merchant_category_chips",
  "headline",
  "promo_badges",
  "details_pills",
  "dates",
  "value_box",
  "spend_box",
  "reward_box",
  "eligibility_box",
  "cta_button",           // CR424: canonical (was source_buttons + claim_button)
  "confidence_indicator",
  "debug_strip",
];

const SECTION_LABELS: Record<string, string> = {
  ribbon: "Ribbon Banner",
  headline: "Headline",
  promo_badges: "Promo Badges",
  details_pills: "Detail Pills",
  dates: "Validity Dates",
  // CR423: Canonical keys with user-friendly labels
  value_box: "Effective Value Box",
  spend_box: "Qualifying Spend Box",
  // CR423: Legacy aliases (for backward compat if old order arrays exist)
  effective_value_box: "Effective Value Box",
  qualifying_spend_box: "Qualifying Spend Box",
  reward_box: "Reward Box",
  eligibility_box: "Eligibility Box",
  // CR424: Canonical action key
  cta_button: "Call-to-Action Button",
  // CR424: Legacy aliases (still resolve if present in old configs)
  source_buttons: "Source Buttons",
  claim_button: "Claim Deal Button",
  why_deal_accordion: "Why This is a Deal",
  merchant_category_chips: "Merchant/Category Chips",
  confidence_indicator: "Confidence Indicator",
  debug_strip: "Debug Strip",
};

// Map section order keys to CardVisibility keys
const SECTION_TO_VISIBILITY: Record<string, keyof CardVisibility> = {
  ribbon: "showRibbon",
  headline: "showHeadline",
  promo_badges: "showPromoBadges",
  details_pills: "showDetailsPills",
  dates: "showDates",
  // CR423: Canonical keys
  value_box: "showEffectiveValueBox",
  spend_box: "showQualifyingSpendBox",
  // CR423: Legacy aliases
  effective_value_box: "showEffectiveValueBox",
  qualifying_spend_box: "showQualifyingSpendBox",
  reward_box: "showRewardBox",
  eligibility_box: "showEligibilityBox",
  // CR424: Canonical action key
  cta_button: "showClaimButton",
  // CR424: Legacy aliases
  source_buttons: "showSourceButtons",
  claim_button: "showClaimButton",
  why_deal_accordion: "showWhyDealAccordion",
  merchant_category_chips: "showMerchantCategoryChips",
  confidence_indicator: "showConfidenceIndicator",
  debug_strip: "showDebugStrip",
};

// ================================================================
// SORTABLE ITEM
// ================================================================

interface SortableItemProps {
  id: string;
  label: string;
  enabled: boolean;
  onToggle: (id: string, enabled: boolean) => void;
}

function SortableItem({ id, label, enabled, onToggle }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-2 rounded border bg-background ${
        isDragging ? "opacity-50 border-primary" : "border-border"
      } ${!enabled ? "opacity-60" : ""}`}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing touch-none"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </button>
      <span className="flex-1 text-sm">{label}</span>
      <button
        type="button"
        onClick={() => onToggle(id, !enabled)}
        className={`p-1 rounded hover:bg-muted ${enabled ? "text-primary" : "text-muted-foreground"}`}
        title={`Toggle visibility (delegates to Visibility Panel)`}
      >
        {enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
      </button>
    </div>
  );
}

// ================================================================
// MAIN COMPONENT
// ================================================================

interface SectionOrderPanelProps {
  sectionOrder: string[];
  visibility: CardVisibility;
  onOrderChange: (newOrder: string[]) => void;
  onVisibilityToggle?: (key: keyof CardVisibility, value: boolean) => void;
  /** CR229: Callback when drag state changes - used to pause preview updates */
  onDragStateChange?: (isDragging: boolean) => void;
}

export function SectionOrderPanel({
  sectionOrder,
  visibility,
  onOrderChange,
  onVisibilityToggle,
  onDragStateChange,
}: SectionOrderPanelProps) {
  // CR229: Track dragging state locally and notify parent
  const [isDragging, setIsDragging] = useState(false);
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // CR229: Small activation distance to prevent accidental drags
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // CR229: Handle drag start - pause preview updates
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setIsDragging(true);
    onDragStateChange?.(true);
  }, [onDragStateChange]);

  // CR229: Handle drag end - update order and resume preview
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    // Always clear dragging state
    setIsDragging(false);
    onDragStateChange?.(false);

    if (over && active.id !== over.id) {
      const oldIndex = sectionOrder.indexOf(active.id as string);
      const newIndex = sectionOrder.indexOf(over.id as string);
      onOrderChange(arrayMove(sectionOrder, oldIndex, newIndex));
    }
  }, [sectionOrder, onOrderChange, onDragStateChange]);

  const handleToggle = useCallback((id: string, enabled: boolean) => {
    const visibilityKey = SECTION_TO_VISIBILITY[id];
    if (visibilityKey && onVisibilityToggle) {
      onVisibilityToggle(visibilityKey, enabled);
    }
  }, [onVisibilityToggle]);

  const handleReset = useCallback(() => {
    onOrderChange([...DEFAULT_SECTION_ORDER]);
  }, [onOrderChange]);

  // Get items with labels and enabled state from unified visibility
  const items: SectionOrderItem[] = sectionOrder.map((id) => {
    const visibilityKey = SECTION_TO_VISIBILITY[id];
    return {
      id,
      label: SECTION_LABELS[id] || id,
      enabled: visibilityKey ? visibility[visibilityKey] : true,
    };
  });

  const enabledCount = items.filter((i) => i.enabled).length;
  const totalCount = items.length;

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <List className="h-4 w-4 text-muted-foreground" />
          <Label className="font-medium">Section Order</Label>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {enabledCount}/{totalCount} visible
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="h-7 px-2"
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Reset
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Drag to reorder sections. Visibility toggles sync with Visibility Panel.
      </p>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={sectionOrder} strategy={verticalListSortingStrategy}>
          <div className="space-y-1">
            {items.map((item) => (
              <SortableItem
                key={item.id}
                id={item.id}
                label={item.label}
                enabled={item.enabled}
                onToggle={handleToggle}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

